import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zeitbuchung, Projekt, Mitarbeiter, Auftrag, AuftragKurz } from '../types';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ZeitbuchungList: React.FC = () => {
  const [buchungen, setBuchungen] = useState<Zeitbuchung[]>([]);
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    ziel_typ: 'auftrag' as 'auftrag' | 'projekt',
    auftrag_id: '',
    projekt_id: '',
    mitarbeiter_id: '',
    datum: new Date().toISOString().split('T')[0],
    stunden: '',
    taetigkeit: '',
    abrechenbar: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [kandidaten, setKandidaten] = useState<AuftragKurz[]>([]);
  const [auswertung, setAuswertung] = useState<Record<string, unknown> | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get<Zeitbuchung[]>(`${API_URL}/api/zeitbuchungen`),
      axios.get<Projekt[]>(`${API_URL}/api/projekte`),
      axios.get<Mitarbeiter[]>(`${API_URL}/api/mitarbeiter`),
      axios.get<Auftrag[]>(`${API_URL}/api/auftraege`),
    ]).then(([zRes, pRes, mRes, aRes]) => {
      setBuchungen(zRes.data);
      setProjekte(pRes.data);
      setMitarbeiter(mRes.data);
      setAuftraege(aRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Nur Aufträge, auf die laut C-Regel (Spec 2026-08-28) noch gebucht werden
  // darf — Storniert/Fakturiert sind Endstatus für Aufwandsbuchungen.
  const buchbareAuftraege = auftraege.filter(a => a.status !== 'Storniert' && a.status !== 'Fakturiert');

  // Gemeinsamer POST-Pfad für den normalen Formular-Submit UND den erneuten
  // Versuch nach Klick auf einen Kandidaten aus der 409-Liste — beide senden
  // dieselben übrigen Formularfelder, nur die Zieladresse unterscheidet sich.
  const buchen = async (auftragId: number | null, projektId: number | null) => {
    setSaving(true);
    setError('');
    setKandidaten([]);
    try {
      await axios.post(`${API_URL}/api/zeitbuchungen`, {
        auftrag_id: auftragId ?? undefined,
        projekt_id: projektId ?? undefined,
        mitarbeiter_id: parseInt(form.mitarbeiter_id),
        stunden: parseFloat(form.stunden),
        taetigkeit: form.taetigkeit || undefined,
        abrechenbar: form.abrechenbar,
        datum: new Date(form.datum).toISOString(),
      });
      setShowForm(false);
      setForm({
        ziel_typ: 'auftrag', auftrag_id: '', projekt_id: '', mitarbeiter_id: '',
        datum: new Date().toISOString().split('T')[0], stunden: '', taetigkeit: '', abrechenbar: true,
      });
      load();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.offene_auftraege) {
        setError(detail.meldung);
        setKandidaten(detail.offene_auftraege);
      } else {
        setError(detail?.meldung || detail || 'Fehler beim Buchen');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    buchen(
      form.ziel_typ === 'auftrag' ? parseInt(form.auftrag_id) : null,
      form.ziel_typ === 'projekt' ? parseInt(form.projekt_id) : null,
    );
  };

  const handleKandidatKlick = (a: AuftragKurz) => {
    setForm(f => ({ ...f, ziel_typ: 'auftrag', auftrag_id: String(a.auftrag_id), projekt_id: '' }));
    buchen(a.auftrag_id, null);
  };

  const toggleForm = () => {
    setError('');
    setKandidaten([]);
    setShowForm(s => !s);
  };

  const loadAuswertung = async () => {
    const r = await axios.get(`${API_URL}/api/zeitbuchungen/auswertung`);
    setAuswertung(auswertung ? null : r.data);
  };

  // AP 3.4: projekt_id kann null sein (direkte Auftrags-Zeitbuchung ohne
  // Projekt, z.B. Auto-Buchung aus einem Einsatz-Abschluss) - additiver
  // Anzeige-Fallback statt "#null".
  const projektName = (id: number | null) =>
    id == null ? 'Direkt am Auftrag' : (projekte.find(p => p.projekt_id === id)?.bezeichnung ?? `#${id}`);
  const auftragName = (id: number) => auftraege.find(a => a.auftrag_id === id)?.auftrag_nr ?? `#${id}`;
  const maName = (id: number) => mitarbeiter.find(m => m.mitarbeiter_id === id)?.name ?? `#${id}`;

  if (loading) return <div style={{ padding: 16 }}>Lade Zeitbuchungen…</div>;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Zeitbuchungen</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="k-btn k-btn--ghost" onClick={loadAuswertung}>
            {auswertung ? 'Auswertung schließen' : 'Auswertung'}
          </button>
          <button
            className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
            onClick={toggleForm}
          >
            {showForm ? 'Abbrechen' : '+ Buchen'}
          </button>
        </div>
      </div>

      {auswertung && (
        <div style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
          <strong>Gesamt: {(auswertung.gesamt_stunden as number).toFixed(1)} h</strong>
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--red, #c0392b)', marginBottom: 12 }}>
          {error}
          {kandidaten.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {kandidaten.map(k => (
                <li key={k.auftrag_id}>
                  <button type="button" className="k-btn k-btn--ghost k-btn--sm" disabled={saving} onClick={() => handleKandidatKlick(k)}>
                    {k.auftrag_nr} – {k.status}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <select value={form.ziel_typ} onChange={e => setForm(f => ({ ...f, ziel_typ: e.target.value as 'auftrag' | 'projekt', auftrag_id: '', projekt_id: '' }))} style={{ padding: 8 }}>
              <option value="auftrag">Auf Auftrag buchen</option>
              <option value="projekt">Auf Baustelle buchen</option>
            </select>
            {form.ziel_typ === 'auftrag' ? (
              <select required value={form.auftrag_id} onChange={e => setForm(f => ({ ...f, auftrag_id: e.target.value }))} style={{ padding: 8 }}>
                <option value="">Auftrag wählen *</option>
                {buchbareAuftraege.map(a => <option key={a.auftrag_id} value={a.auftrag_id}>{a.auftrag_nr} – {a.status}</option>)}
              </select>
            ) : (
              <select required value={form.projekt_id} onChange={e => setForm(f => ({ ...f, projekt_id: e.target.value }))} style={{ padding: 8 }}>
                <option value="">Baustelle wählen *</option>
                {projekte.map(p => <option key={p.projekt_id} value={p.projekt_id}>{p.projekt_nr} – {p.bezeichnung}</option>)}
              </select>
            )}
            <select required value={form.mitarbeiter_id} onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))} style={{ padding: 8 }}>
              <option value="">Mitarbeiter wählen *</option>
              {mitarbeiter.map(m => <option key={m.mitarbeiter_id} value={m.mitarbeiter_id}>{m.name}</option>)}
            </select>
            <input required type="date" value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))} style={{ padding: 8 }} />
            <input required placeholder="Stunden *" type="number" step="0.25" min="0.25" value={form.stunden} onChange={e => setForm(f => ({ ...f, stunden: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="Tätigkeit" value={form.taetigkeit} onChange={e => setForm(f => ({ ...f, taetigkeit: e.target.value }))} style={{ padding: 8 }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input type="checkbox" checked={form.abrechenbar} onChange={e => setForm(f => ({ ...f, abrechenbar: e.target.checked }))} />
            Abrechenbar
          </label>
          <button type="submit" className="k-btn k-btn--primary" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </form>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bone-3)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Mitarbeiter</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Auftrag / Baustelle</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Stunden</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tätigkeit</th>
            <th style={{ padding: '8px 12px', textAlign: 'center' }}>Abr.</th>
          </tr>
        </thead>
        <tbody>
          {buchungen.map(b => (
            <React.Fragment key={b.buchung_id}>
              <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                <td style={{ padding: '8px 12px' }}>{new Date(b.datum).toLocaleDateString('de-DE')}</td>
                <td style={{ padding: '8px 12px' }}>{maName(b.mitarbeiter_id)}</td>
                <td style={{ padding: '8px 12px' }}>{b.auftrag_id ? auftragName(b.auftrag_id) : projektName(b.projekt_id)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>{b.stunden.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', color: 'var(--ink-2)' }}>{b.taetigkeit || '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  {b.abrechenbar ? '✓' : '—'}
                  <button
                    className="k-btn k-btn--ghost k-btn--sm"
                    style={{ marginLeft: 6 }}
                    onClick={() => setAkteId(akteId === b.buchung_id ? null : b.buchung_id)}
                  >
                    {akteId === b.buchung_id ? 'Akte schließen' : 'Akte'}
                  </button>
                </td>
              </tr>
              {akteId === b.buchung_id && (
                <tr>
                  <td colSpan={6} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                    <AktivitaetenBlock entitaetTyp="zeitbuchung" entitaetId={b.buchung_id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {buchungen.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Keine Zeitbuchungen vorhanden.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ZeitbuchungList;
